import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "next-i18next";

import MarketClosedFeedback from "@/components/SystemFeedbackMobile/MarketClosedFeedback";
import InsufficientFundsFeedback from "@/components/SystemFeedbackMobile/InsufficientFundsFeedback";
import DeleteOrderFeedback from "@/components/SystemFeedbackMobile/DeleteOrderFeedback";
import MarginCallFeedback from "@/components/SystemFeedbackMobile/MarginCallFeedback";
import SuccessFeedback from "@/components/SystemFeedbackMobile/common/SuccessFeedback";
import TradeDisabledFeedback from "@/components/SystemFeedbackMobile/TradeDisabledFeedback";
import OrderInfoView from "@/components/SystemFeedbackMobile/common/OrderInfoView";
import DangerFeedback from "@/components/SystemFeedbackMobile/common/DangerFeedback";
import WarningFeedback from "@/components/SystemFeedbackMobile/common/WarningFeedback";
import CloseOrderModal from "@/components/SystemFeedbackMobile/CloseOrderModal";

import { useSystemFeedback } from "@/redux/selectors/systemFeedbackSelector";
import { resetFeedbackModals } from "@/redux/actions/systemFeedback";
import { useUI } from "@/redux/selectors/uiSelector";
import { useAccountInfo } from "@/redux/selectors/accountSelector";
import { useTradeInfo } from "@/redux/selectors/tradeInfoSelector";
import { useMarketSchedule } from "@/hooks/trade/schedule";
import { useTradeOrders } from "@/redux/selectors/ordersSelector";
import { closeTradeOrderModal } from "@/redux/actions/ui";

import {
    IOrderErrorModalParams,
    ITradeOrderModalTypes,
    IOrderSuccessModalParams,
} from "@/redux/interfaces/IUI";

import parentFrameAPI from "@/services/parent";
import { FavoritesAlert } from "./FavoritesAlert";

import styles from "./SystemFeedback.module.scss";

const SystemFeedbackMobileWrapper = ({ children }): JSX.Element => {
    const { t } = useTranslation("common");
    const dispatch = useDispatch();
    const { tradeOrderModal, tradeOrderModalParams } = useUI();
    const { isGuest, isLazyMode } = useAccountInfo();
    const { activeSymbolId, symbolsInfoById } = useTradeInfo();
    const { isMarketOpen } = useMarketSchedule(activeSymbolId);
    const { volume } = useTradeOrders();
    const symbolInfo = symbolsInfoById[activeSymbolId];

    const {
        successfullyClosedOrder,
        successfullyModifiedOrder,
        successfullyDeletedOrder,
        successfullyOpenedOrder,
        isShowOpenPositionWarning,
        generalErrorMessage,
        isShowSecureConnection,
        isShowMarginCall,
        isShowNoQuotes,
        isShowOrderFailed,
        isShowOrderHasWrongPrice,
        favoritesAlerts,
    } = useSystemFeedback();

    const isSuccessParams = (params: unknown): params is IOrderSuccessModalParams =>
        params !== null && typeof params === "object" && "order" in params;

    const order =
        successfullyOpenedOrder ||
        (isSuccessParams(tradeOrderModalParams) ? tradeOrderModalParams.order : undefined);

    useEffect(() => {
        dispatch(resetFeedbackModals());
        dispatch(closeTradeOrderModal());
    }, []);

    const handleClose = () => {
        dispatch(closeTradeOrderModal());
        dispatch(resetFeedbackModals());
    };

    const handleDeposit = () => {
        if (isGuest && !isLazyMode) {
            parentFrameAPI.login();
        } else {
            parentFrameAPI.deposit();
        }
        dispatch(closeTradeOrderModal());
    };

    const determineModalType = () => {
        if (successfullyOpenedOrder) return ITradeOrderModalTypes.OPEN_ORDER_SUCCESS;
        if (successfullyModifiedOrder) return ITradeOrderModalTypes.ORDER_MODIFIED_SUCCESS;
        if (successfullyClosedOrder) return ITradeOrderModalTypes.CLOSE_TRADE_SUCCESS;
        if (successfullyDeletedOrder) return ITradeOrderModalTypes.ORDER_DELETE_SUCCESS;
        return tradeOrderModal;
    };

    const currentModalType = determineModalType();

    const renderFeedbackModal = () => {
        if (isShowSecureConnection) {
            return (
                <WarningFeedback
                    onClose={handleClose}
                    title={t("modals.notification.secure.connection")}
                />
            );
        }

        if (isShowNoQuotes) {
            return <WarningFeedback onClose={handleClose} title={t("messages.136")} />;
        }

        if (isShowOrderFailed) {
            return <WarningFeedback onClose={handleClose} title={t("messages.orderFailed")} />;
        }

        if (isShowOrderHasWrongPrice) {
            return <WarningFeedback onClose={handleClose} title={t("messages.129")} />;
        }
        if (isShowMarginCall || currentModalType === ITradeOrderModalTypes.MARGIN_CALL) {
            return <MarginCallFeedback onClose={handleClose} onDeposit={handleDeposit} />;
        }

        if (isShowOpenPositionWarning) {
            return (
                <WarningFeedback
                    onClose={handleClose}
                    title={t("guidance.step03.title")}
                    description={t("modals.error.open.position.description")}
                />
            );
        }

        if (favoritesAlerts.length > 0) {
            return (
                <FavoritesAlert
                    messageData={favoritesAlerts[0]}
                    count={favoritesAlerts.length}
                    isMarketOpen={isMarketOpen}
                />
            );
        }

        const modalMap = {
            [ITradeOrderModalTypes.REQUEST_PLACED]: <WarningFeedback placed onClose={handleClose} />,
            [ITradeOrderModalTypes.OPEN_ORDER_SUCCESS]: (
                <SuccessFeedback
                    onClose={handleClose}
                    title={t("successfuly.opened")}
                    className={styles.orderFeedback}>
                    <OrderInfoView order={order} operation="open" volume={order?.lots} />
                </SuccessFeedback>
            ),
            [ITradeOrderModalTypes.ORDER_MODIFIED_SUCCESS]: (
                <SuccessFeedback
                    onClose={handleClose}
                    title={t("successfuly.modified")}
                    className={styles.orderFeedback}>
                    <OrderInfoView
                        volume={successfullyModifiedOrder?.lots}
                        order={successfullyModifiedOrder}
                        operation="open"
                    />
                </SuccessFeedback>
            ),
            [ITradeOrderModalTypes.CLOSE_TRADE_SUCCESS]: (
                <SuccessFeedback
                    onClose={handleClose}
                    title={t("successfuly.closed")}
                    className={styles.orderFeedback}>
                    <OrderInfoView
                        order={
                            successfullyClosedOrder ||
                            (isSuccessParams(tradeOrderModalParams)
                                ? tradeOrderModalParams.order
                                : undefined)
                        }
                        operation="close"
                        volume={
                            //@ts-ignore
                            volume || successfullyClosedOrder?.lots || tradeOrderModalParams?.order?.lots
                        }
                    />
                </SuccessFeedback>
            ),
            [ITradeOrderModalTypes.ORDER_DELETE_SUCCESS]: (
                <SuccessFeedback
                    onClose={handleClose}
                    title={t("successfuly.deleted")}
                    className={styles.orderFeedback}>
                    <OrderInfoView
                        volume={volume || successfullyDeletedOrder?.lots}
                        order={successfullyDeletedOrder}
                        operation="delete"
                    />
                </SuccessFeedback>
            ),
            [ITradeOrderModalTypes.MARKET_CLOSED]: <MarketClosedFeedback onClose={handleClose} />,
            [ITradeOrderModalTypes.TRADE_DISABLED_USER_READ_ONLY]: (
                <TradeDisabledFeedback isReadOnly onClose={handleClose} />
            ),
            [ITradeOrderModalTypes.TRADE_DISABLED_ASSET_CLOSE_ONLY]: (
                <TradeDisabledFeedback isCloseOnly onClose={handleClose} />
            ),
            [ITradeOrderModalTypes.INSUFFICIENT_FUNDS]: (
                <InsufficientFundsFeedback onDeposit={handleDeposit} onClose={handleClose} />
            ),
            [ITradeOrderModalTypes.ORDER_DELETE_CONFIRM]: <DeleteOrderFeedback />,
            [ITradeOrderModalTypes.INVALID_PARAMS]: (
                <DangerFeedback
                    title={t("messages.3")}
                    onClose={handleClose}
                    description={t("modals.error.invalidParams.description", {
                        points: symbolInfo?.ex?.stopsLevel || 50,
                    })}
                />
            ),
            [ITradeOrderModalTypes.NO_CONNECT]: (
                <DangerFeedback title={t("no.connection")} description="" onClose={handleClose} />
            ),
            [ITradeOrderModalTypes.INVALID_SL_TP]: (
                <WarningFeedback
                    onClose={handleClose}
                    title={t("messages.130")}
                    description={t("modals.error.invalidParams.description", {
                        points: symbolInfo?.ex?.stopsLevel || 50,
                    })}
                />
            ),
            [ITradeOrderModalTypes.ORDER_ERROR]: (
                <WarningFeedback
                    onClose={handleClose}
                    title={t("modals.error.title")}
                    description={
                        generalErrorMessage || (tradeOrderModalParams as IOrderErrorModalParams)?.error
                    }
                />
            ),
            [ITradeOrderModalTypes.INVALID_PARAMETERS_WRONG_EXPIRE_DATE]: (
                <DangerFeedback
                    title={t("messages.3")}
                    onClose={handleClose}
                    description={t("modals.error.tradeDisabled.invalid.expiration.date")}
                />
            ),
            [ITradeOrderModalTypes.CLOSE_TRADE]: <CloseOrderModal />,
            default:
                generalErrorMessage || currentModalType ? (
                    <WarningFeedback
                        onClose={handleClose}
                        title={t("modals.error.title")}
                        description={
                            generalErrorMessage ||
                            (tradeOrderModalParams as IOrderErrorModalParams)?.error
                        }
                    />
                ) : null,
        };

        return modalMap[currentModalType] ?? modalMap.default;
    };

    return (
        <>
            {children}
            {renderFeedbackModal()}
        </>
    );
};

export default SystemFeedbackMobileWrapper;
